import { Search, Bell, Menu, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import AreaUserMenu from "@/components/common/AreaUserMenu";

interface AppHeaderProps {
  onMenuClick: () => void;
  userName?: string;
  userRole?: string;
}

const breadcrumbMap: Record<string, string> = {
  "/admin-dashboard": "Dashboard",
  "/admin-dashboard/events": "Events",
  "/admin-dashboard/events/new": "Create Event",
  "/admin-dashboard/events/finance": "Finance",
  "/admin-dashboard/events/analytics": "Analytics",
  "/admin-dashboard/events/registration-forms": "Registration Forms",
  "/admin-dashboard/events/packet-management": "Packet Management",
  "/admin-dashboard/events/faqs": "FAQs",
  "/admin-dashboard/events/special-requests": "Special Requests",
  "/admin-dashboard/venues": "Venues",
  "/admin-dashboard/event-sponsors": "Sponsors",
  "/admin-dashboard/settings": "Settings",
  "/admin-dashboard/users": "Users",
  "/admin-dashboard/roles": "Roles",
  "/admin-dashboard/tournaments": "Tournaments",
  "/admin-dashboard/notifications": "Notifications",
  "/admin-dashboard/hub": "Hub",
  "/admin": "Admin",
  "/admin/staff": "Staff Management",
  "/admin/organizations": "Organizations",
  "/admin/events": "Events",
};

function getBreadcrumbs(pathname: string): { label: string; href: string }[] {
  const crumbs: { label: string; href: string }[] = [
    { label: "Dashboard", href: "/admin-dashboard" },
  ];
  if (pathname === "/admin-dashboard") return crumbs;

  // Build breadcrumbs from path segments
  const parts = pathname.split("/").filter(Boolean);
  let path = "";
  for (const part of parts) {
    path += `/${part}`;
    if (path === "/admin-dashboard") continue; // skip, already added as root
    if (path === "/admin") continue; // skip bare /admin prefix
    const label =
      breadcrumbMap[path] ||
      part
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    crumbs.push({ label, href: path });
  }
  return crumbs;
}

export function AppHeader({
  onMenuClick,
  userName = "Admin",
  userRole = "Admin",
}: AppHeaderProps) {
  const location = useLocation();
  const breadcrumbs = getBreadcrumbs(location.pathname);
  const title =
    breadcrumbMap[location.pathname] ||
    breadcrumbs.at(-1)?.label ||
    "Page";

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-border px-4 sm:px-6 lg:px-8">
      <div className="flex h-14 items-center gap-4">
        {/* Mobile hamburger */}
        <button
          onClick={onMenuClick}
          className="lg:hidden text-foreground hover:text-primary transition-colors flex-shrink-0"
        >
          <Menu size={22} />
        </button>

        {/* Page title + breadcrumbs */}
        <div className="flex flex-col justify-center min-w-0">
          {/* Breadcrumbs */}
          <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground mb-0.5">
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.href} className="flex items-center gap-1">
                {i > 0 && <ChevronRight size={10} />}
                {i < breadcrumbs.length - 1 ? (
                  <Link
                    to={crumb.href}
                    className="hover:text-foreground transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-foreground">{crumb.label}</span>
                )}
              </span>
            ))}
          </div>
          <h1 className="text-lg font-bold text-foreground leading-tight">
            {title}
          </h1>
        </div>

        {/* Search (center) */}
        <div className="flex-1 flex justify-center max-w-xs sm:max-w-sm lg:max-w-md mx-auto">
          <div className="relative w-full hidden sm:block">
            <input
              type="text"
              placeholder="Search anything"
              className="w-full bg-secondary border-0 rounded-full pl-4 pr-10 py-2 text-sm placeholder-muted-foreground text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        {/* Right: Notification, User */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto">
          {/* Mobile search */}
          <button className="sm:hidden text-foreground hover:text-primary">
            <Search size={20} />
          </button>

          {/* Notification bell */}
          <button className="relative flex h-9 w-9 items-center justify-center rounded-full bg-secondary hover:bg-muted transition-colors">
            <Bell size={18} className="text-foreground" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" />
          </button>

          {/* User avatar + switch-areas/account menu */}
          <AreaUserMenu roleLabel={userRole} settingsPath="/admin-dashboard/settings" />
        </div>
      </div>
    </header>
  );
}
